import { useState } from 'react';
import { useActivePlayer } from '@/context/ActivePlayerContext';
import { useBackgroundSync } from '@/context/BackgroundSyncContext';
import SpinnerOverlay from '@/common/SpinnerOverlay';
import DeletedHistory from '../DeletedHistory';
import IronList from './IronList';
import IronSet from './IronSet';
import { useEditIron } from './useEditIron';
import useOnlineStatus from '@/hooks/useOnlineStatus';
import { TourBagIronDetail, TourBagIronSet } from '@/api/models';
import { createEmptyTourBagIronSet } from '@/utils';
import { headTypeMap } from '@/constants';
import clubStyles from '../Club.module.scss';
// Reuse Iron components
import NewSetModal from '../Iron/NewSetModal';
import ClubHistory from '../ClubHistory/ClubHistory';
import OfflineModal from '@/common/OfflineModal';

function Iron() {
  const { tourBag, player, permissions } = useActivePlayer();
  const tourPageSettings = permissions?.tourPageSettings;
  const { isSyncComplete } = useBackgroundSync();
  const isOnline = useOnlineStatus();
  const [showSpinner, setShowSpinner] = useState(false);
  const [ironToEdit, setIronToEdit] = useState<TourBagIronDetail | null>(null);
  const [ironToCreate, setIronToCreate] = useState<TourBagIronDetail | null>(null);
  const [showCreateIron, setShowCreateIron] = useState(false);
  const [ironSetToEdit, setIronSetToEdit] = useState<TourBagIronSet | null>(null);
  const [ironSetToCreate, setIronSetToCreate] = useState<TourBagIronSet | null>(null);
  const [showNewSetModal, setShowNewSetModal] = useState(false);
  const [showDeletedHistory, setShowDeletedHistory] = useState(false);
  const [ironForHistory, setIronForHistory] = useState<TourBagIronDetail | null>(null);
  const [showPreEditOfflineModal, setShowPreEditOfflineModal] = useState(false);
  const [offlineIronOrSet, setOfflineIronOrSet] = useState(''); //'Iron' or 'Set'

  const clubDeletedPermission = permissions?.tourBagSettings?.clubDeleted ?? false;

  // We find the Utility headTypeDescription instead of Iron
  const ironBag = tourBag?.find((bag) => bag.headTypeDescription === 'Utility');
  const ironClubs = ironBag?.tourBagIronDetail || [];
  const ironSets = ironBag?.tourBagIronSets || [];
  const showClubs = !ironToEdit && !ironToCreate && !showCreateIron && !ironSetToEdit && !ironSetToCreate;
  const {
    ironClubData,
    handDropdownData,
    loftDropdownData,
    lieDropdownData,
    ironStaticDropdownData,
    ironDynamicDropdownData,
    shaftDropdownData,
    shaftLengthDropdownData,
    swingWtDropdownData,
    roundRibDropdownData,
    logoDropdownData,
    shaftTippingData,
  } = useEditIron();

  const handleAddNewSet = (name: string) => {
    const emptyIronSet = createEmptyTourBagIronSet(name);
    setShowNewSetModal(false);
    setIronSetToCreate(emptyIronSet);
  };

  const handleIronForHistory = (iron: TourBagIronDetail | null) => {
    setIronForHistory(iron);
  };

  const closeClubHistoryModal = () => {
    setIronForHistory(null);
  };

  const openOfflineEditModal = (ironOrSet: string) => {
    setOfflineIronOrSet(ironOrSet);
    setShowPreEditOfflineModal(true);
  };

  const showEditFormOfflineModal = () => {
    setShowPreEditOfflineModal(false);
    if (offlineIronOrSet == 'Iron') {
      setShowCreateIron(true);
    } else {
      setShowNewSetModal(true);
    }
  };

  return (
    // Iron Container
    <div className={`${clubStyles.container} ${!showClubs ? clubStyles.editingBackground : ''}`}>
      {!ironClubs?.length && !ironSets?.length && showClubs && <div className={clubStyles.noClubs}>No Irons in System</div>}
      {/* Iron List */}
      {!ironSetToEdit && (
        <IronList
          showClubs={showClubs}
          setShowSpinner={setShowSpinner}
          loftDropdownData={loftDropdownData}
          lieDropdownData={lieDropdownData}
          ironStaticDropdownData={ironStaticDropdownData}
          ironDynamicDropdownData={ironDynamicDropdownData}
          shaftDropdownData={shaftDropdownData}
          shaftLengthDropdownData={shaftLengthDropdownData}
          swingWtDropdownData={swingWtDropdownData}
          handDropdownData={handDropdownData}
          roundRibDropdownData={roundRibDropdownData}
          logoDropdownData={logoDropdownData}
          shaftTippingData={shaftTippingData}
          setIronToCreate={setIronToCreate}
          ironToCreate={ironToCreate}
          showCreateIron={showCreateIron}
          setShowCreateIron={setShowCreateIron}
          ironToEdit={ironToEdit}
          setIronToEdit={setIronToEdit}
          handleIronForHistory={handleIronForHistory}
        />
      )}
      {/* Iron Set */}
      {!ironToEdit && (
        <IronSet
          showClubs={showClubs}
          setShowSpinner={setShowSpinner}
          ironClubData={ironClubData}
          loftDropdownData={loftDropdownData}
          lieDropdownData={lieDropdownData}
          ironStaticDropdownData={ironStaticDropdownData}
          ironDynamicDropdownData={ironDynamicDropdownData}
          shaftDropdownData={shaftDropdownData}
          shaftLengthDropdownData={shaftLengthDropdownData}
          swingWtDropdownData={swingWtDropdownData}
          handDropdownData={handDropdownData}
          roundRibDropdownData={roundRibDropdownData}
          logoDropdownData={logoDropdownData}
          shaftTippingData={shaftTippingData}
          ironSetToCreate={ironSetToCreate}
          setIronSetToCreate={setIronSetToCreate}
          ironSetToEdit={ironSetToEdit}
          setIronSetToEdit={setIronSetToEdit}
        />
      )}
      {showClubs && tourPageSettings?.iron === 'full' && isSyncComplete && (
        <div className={`${clubStyles.addNewWrapper} ${clubStyles.buttonGroup}`}>
          <button className={clubStyles.addNew} onClick={!isOnline ? () => openOfflineEditModal('Iron') : () => setShowCreateIron(true)} aria-label="Add New Iron">
            + Add New
          </button>
          <button className={clubStyles.addNew} onClick={!isOnline ? () => openOfflineEditModal('Set') : () => setShowNewSetModal(true)} aria-label="Add New Iron Set">
            + Add Set
          </button>
          {isOnline && clubDeletedPermission && (
            <button className={clubStyles.addNew} onClick={() => setShowDeletedHistory(true)} aria-label="Show Deleted Irons">
              Show Deleted
            </button>
          )}
        </div>
      )}
      {showNewSetModal && <NewSetModal showNewSetModal={showNewSetModal} closeNewSetModal={() => setShowNewSetModal(false)} ironSets={ironSets} onAddNewSet={handleAddNewSet} />}
      {showDeletedHistory && (
        <DeletedHistory
          headTypeId={headTypeMap['Utility']}
          playerId={ironBag?.playerId || 0}
          playerName={ironBag?.playerName || ''}
          showDeletedHistory={showDeletedHistory}
          closeDeletedHistory={() => setShowDeletedHistory(false)}
        />
      )}
      {ironForHistory && (
        <ClubHistory
          tourBagMstrId={ironForHistory.tourBagMstrId}
          rowGuid={ironForHistory.rowGuid}
          headTypeId={headTypeMap['Iron']}
          player={player}
          model={ironForHistory.headModelName}
          closeModal={closeClubHistoryModal}
        />
      )}
      {showSpinner && <SpinnerOverlay />}
      <OfflineModal onCancel={() => setShowPreEditOfflineModal(false)} onClose={showEditFormOfflineModal} isOpen={showPreEditOfflineModal} modalType={'IsOffline'} />
    </div>
  );
}

export default Iron;
